import React, { useEffect, useState, useMemo } from 'react'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import {
  Search, Trash2, Mail, Phone, Building,
  ArrowLeft, Users, X, SlidersHorizontal
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '@/contexts/ToastContext'
import { projectsApi, GlobalContact } from '@/lib/projectsApi'
import { contactDisplay } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

export const ContactsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { success, error: showError } = useToast()

  const [contacts, setContacts] = useState<GlobalContact[]>([])
  const [loading, setLoading] = useState(true)
  // ?q= deep-link (smart-search click-through): land pre-filtered to the contact
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '')
  const [selectedCompany, setSelectedCompany] = useState('')
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '')
  const [contactToDelete, setContactToDelete] = useState<GlobalContact | null>(null)

  const fetchContacts = async () => {
    setLoading(true)
    try {
      setContacts(await projectsApi.listContacts())
    } catch (err: any) {
      showError(err.message ?? t('contactsPage.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchContacts() }, [t])

  const companyOptions = useMemo(() =>
    [...new Set(contacts.map(c => c.company_name).filter(Boolean))].sort() as string[]
  , [contacts])

  const projectOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const c of contacts) {
      for (const p of c.linked_projects) {
        if (!seen.has(p.project_id)) {
          seen.set(p.project_id, `${p.shipyard_name ?? t('contactsPage.unknown')} / ${p.hull_number}`)
        }
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [contacts, t])

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    return contacts.filter(c => {
      if (term) {
        const text = [c.full_name, c.email, c.phone, c.company_name,
          ...(c.shipyard_contacts ?? []),
          ...c.linked_systems.map(s => s.system_name),
          ...c.linked_projects.map(p => p.hull_number)
        ].filter(Boolean).join(' ').toLowerCase()
        if (!text.includes(term)) return false
      }
      if (selectedCompany && c.company_name !== selectedCompany) return false
      if (selectedProject && !c.linked_projects.some(p => p.project_id === selectedProject)) return false
      return true
    })
  }, [contacts, searchTerm, selectedCompany, selectedProject])

  const groupedByCompany = useMemo(() => {
    const map = new Map<string, GlobalContact[]>()
    for (const c of filtered) {
      const key = c.company_name?.trim() || t('contactsPage.noCompany')
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(c)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [filtered, t])

  const stats = useMemo(() => ({
    total: contacts.length,
    companies: new Set(contacts.map(c => c.company_name).filter(Boolean)).size,
    projects: new Set(contacts.flatMap(c => c.linked_projects.map(p => p.project_id))).size,
    withEmail: contacts.filter(c => c.email).length,
  }), [contacts])

  const handleDelete = async (contact: GlobalContact) => {
    try {
      await projectsApi.deleteContact(contact.id)
      success(t('contactsPage.deleted'))
      setContacts(prev => prev.filter(c => c.id !== contact.id))
    } catch (err: any) {
      showError(err.message ?? t('contactsPage.deleteFailed'))
    }
  }

  const activeFilterCount = [searchTerm, selectedCompany, selectedProject].filter(Boolean).length

  return (
    <div className="min-h-screen bg-[#f4f6f8] text-slate-900 dark:bg-slate-900 dark:text-white">
      <div className="mx-auto max-w-[1680px] px-4 py-6 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-5 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} aria-label={t('common.backToDashboard')}>
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{t('nav.dashboard')}</span>
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em]">
              <Users className="h-5 w-5 text-blue-800" />
              {t('contactsPage.title')}
            </h1>
            <p className="text-xs text-slate-400 dark:text-slate-500">{t('contactsPage.subtitle')}</p>
          </div>
        </div>

        {/* Stats bar */}
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 dark:border-slate-700 dark:bg-slate-800/50">
          <div><span className="mr-1 text-[10px] uppercase text-slate-400">{t('contactsPage.contacts')}</span><span className="text-sm font-medium tabular-nums">{stats.total}</span></div>
          <span className="text-slate-300 dark:text-slate-600" aria-hidden>·</span>
          <div><span className="mr-1 text-[10px] uppercase text-slate-400">{t('contactsPage.companies')}</span><span className="text-sm font-medium tabular-nums">{stats.companies}</span></div>
          <span className="text-slate-300 dark:text-slate-600" aria-hidden>·</span>
          <div><span className="mr-1 text-[10px] uppercase text-slate-400">{t('contactsPage.projects')}</span><span className="text-sm font-medium tabular-nums">{stats.projects}</span></div>
          <span className="text-slate-300 dark:text-slate-600" aria-hidden>·</span>
          <div><span className="mr-1 text-[10px] uppercase text-slate-400">{t('contactsPage.withEmail')}</span><span className="text-sm font-medium tabular-nums">{stats.withEmail}</span></div>
        </div>

        {/* Filters */}
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] dark:border-slate-700 dark:bg-slate-800/50">
          <SlidersHorizontal className="hidden h-4 w-4 text-slate-400 sm:block" aria-hidden />
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              placeholder={t('contactsPage.searchPlaceholder')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="h-9 w-full rounded-md border border-slate-300 bg-white pl-9 pr-9 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label={t('contactsPage.clearSearch')}>
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <select
            value={selectedCompany}
            onChange={e => setSelectedCompany(e.target.value)}
            className="h-9 min-w-[160px] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm outline-none hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('contactsPage.allCompanies')}</option>
            {companyOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select
            value={selectedProject}
            onChange={e => setSelectedProject(e.target.value)}
            className="h-9 min-w-[160px] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 shadow-sm outline-none hover:border-slate-400 focus:border-blue-700 focus:ring-2 focus:ring-blue-700/15 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          >
            <option value="">{t('contactsPage.allProjects')}</option>
            {projectOptions.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(''); setSelectedCompany(''); setSelectedProject('') }}>
              {t('contactsPage.clearFilters')}
            </Button>
          )}
        </div>

        {selectedProject && projectOptions.find(([pid]) => pid === selectedProject) && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-800/50">
            <span className="text-slate-600 dark:text-slate-300">
              {t('contactsPage.showingFor')} <span className="font-medium text-slate-900 dark:text-white">{projectOptions.find(([pid]) => pid === selectedProject)?.[1]}</span>
            </span>
            <button
              onClick={() => { setSelectedProject(''); navigate('/contacts', { replace: true }) }}
              className="ml-auto text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {t('contactsPage.showAll')}
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="h-28 animate-pulse rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/50" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-16 text-center dark:border-slate-700 dark:bg-slate-800/30">
            <Users className="mx-auto mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {activeFilterCount > 0 ? t('contactsPage.noMatch') : t('contactsPage.empty')}
            </p>
            {activeFilterCount > 0 && <button onClick={() => { setSearchTerm(''); setSelectedCompany(''); setSelectedProject('') }} className="mt-2 text-sm font-medium text-blue-700 hover:underline">{t('contactsPage.clearAllFilters')}</button>}
          </div>
        ) : (
          <div className="space-y-3">
            {groupedByCompany.map(([company, companyContacts]) => (
              <div key={company}>
                {/* Company group header */}
                <div className="flex items-center gap-2 pb-1.5 mb-1.5 border-b border-slate-200 dark:border-slate-700">
                  <Building className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-200">{company}</span>
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    {t('contactsPage.contactCount', { count: companyContacts.length })}
                  </span>
                </div>

                {/* Cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                  {companyContacts.map(c => (
                    <div
                      key={c.id}
                      className="group relative rounded-md border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-[border-color,box-shadow] hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-slate-600"
                    >
                      {/* Delete button */}
                      <button
                        onClick={() => setContactToDelete(c)}
                        className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded text-slate-300 opacity-100 transition hover:bg-red-50 hover:text-red-600 focus:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                        aria-label={t('contactsPage.deleteNamed', { name: c.full_name || t('contactsPage.contact') })}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>

                      {/* Top: avatar + name */}
                      <div className="flex items-start gap-2 mb-2.5">
                        {(() => {
                          const { initials, showFull } = contactDisplay(c.full_name)
                          return (
                            <div
                              className={`rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center font-medium text-slate-500 dark:text-slate-400 flex-shrink-0 text-[10px] ${
                                showFull ? 'h-7 min-w-[28px] w-auto px-1.5' : 'w-7 h-7'
                              }`}
                            >
                              {initials}
                            </div>
                          )
                        })()}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-slate-900 dark:text-white leading-tight">
                            {c.full_name || t('contactsPage.unnamed')}
                          </div>
                          <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">{company}</div>
                        </div>
                      </div>

                      <div className="border-t border-slate-100 dark:border-slate-700 my-2" />

                      {/* Contact details */}
                      <div className="space-y-1">
                        {c.email ? (
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <Mail className="h-3 w-3 text-slate-400 flex-shrink-0" />
                            <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()}
                               className="text-blue-600 dark:text-blue-400 hover:underline truncate">
                              {c.email}
                            </a>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-[11px] text-slate-300 dark:text-slate-600">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            {t('contactsPage.noEmail')}
                          </div>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-[11px] tabular-nums text-slate-500 hover:text-blue-700 dark:text-slate-400">
                            <Phone className="h-3 w-3 text-slate-400 flex-shrink-0" />
                            {c.phone}
                          </a>
                        )}
                      </div>

                      {/* the yard's own person for this contact, where an
                          SHI form supplied one. Silent otherwise. */}
                      {c.shipyard_contacts && c.shipyard_contacts.length > 0 && (
                        <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                          <span className="text-slate-400">{t('contactsPage.yardPic')}:</span>{' '}
                          <span className="font-medium text-slate-600 dark:text-slate-300">
                            {c.shipyard_contacts.join(', ')}
                          </span>
                          {c.shipyard_contact_emails && c.shipyard_contact_emails.length > 0 && (
                            <span className="ml-1 text-slate-400">
                              ({c.shipyard_contact_emails.join(', ')})
                            </span>
                          )}
                        </div>
                      )}

                      {/* Linked systems */}
                      {c.linked_systems.length > 0 && (
                        <div className="mt-2 border-t border-slate-100 pt-1.5 dark:border-slate-700">
                          <div className="mb-1 text-[10px] uppercase text-slate-400 dark:text-slate-500">
                            {t('contactsPage.linkedSystems')}
                          </div>
                          <div className="space-y-1">
                            {c.linked_systems.slice(0, 4).map((s, i) => {
                              const proj = c.linked_projects.find(p => p.project_id === s.project_id)
                              return (
                                <div key={i} className="flex items-center gap-1.5 text-[10px]">
                                  <span className="min-w-0 flex-1 truncate text-slate-600 dark:text-slate-300">
                                    {s.system_name || '—'}
                                  </span>
                                  {proj && (
                                    <button
                                      onClick={() => navigate(`/projects/${proj.project_id}`)}
                                      className="shrink-0 rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-medium tabular-nums text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                                    >
                                      {proj.hull_number}
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                            {c.linked_systems.length > 4 && (
                              <div className="text-[10px] text-slate-400">
                                {t('contactsPage.more', { count: c.linked_systems.length - 4 })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        isOpen={contactToDelete !== null}
        title={t('contactsPage.deleteTitle')}
        message={t('contactsPage.deleteMessage', { name: contactToDelete?.full_name || t('contactsPage.thisContact') })}
        confirmText={t('contactsPage.deleteContact')}
        onCancel={() => setContactToDelete(null)}
        onConfirm={() => {
          if (contactToDelete) void handleDelete(contactToDelete)
        }}
      />
    </div>
  )
}
